import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { useNavigate } from 'react-router-dom';
import CreateCompanyModal from '../components/CreateCompanyModal';
import { getEffectiveCompanyId } from '../utils/userCompany';
import LoadingSpinner from '../components/LoadingSpinner';
import './NoCompany.css';

const NoCompany = () => {
  const { t } = useTranslation('public');
  const { user, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState('');
  const [joiningCompanyId, setJoiningCompanyId] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (getEffectiveCompanyId(user)) {
      navigate('/dashboard');
      return;
    }
    fetchCompanies();

    const urlParams = new URLSearchParams(window.location.search);
    const invited = urlParams.get('invited');
    if (invited === 'true') {
      setNotification({
        type: 'info',
        message: t('newInvitationNotice')
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user, navigate, t]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies/invitations');
      setCompanies(response.data.data);
    } catch (fetchError) {
      console.error('Error fetching companies:', fetchError);
      setError(formatError(fetchError) || t('failedFetchCompanies'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompanySuccess = async () => {
    await fetchUser();
    setShowCreateModal(false);
    navigate('/dashboard');
  };

  const handleJoinCompany = async (companyId) => {
    setJoiningCompanyId(companyId);
    setError('');

    try {
      await axios.post(`/api/companies/${companyId}/join`);
      await fetchUser();
      navigate('/dashboard');
    } catch (joinError) {
      setError(formatError(joinError) || t('failedJoinCompany'));
      setJoiningCompanyId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner className="no-company-loading" />;
  }

  return (
    <div className="no-company-page">
      <div className="no-company-container">
        <h1 className="no-company-title">{t('noCompanyTitle')}</h1>
        <p className="no-company-subtitle">{t('noCompanySubtitle')}</p>

        {error && <div className="no-company-error">{error}</div>}

        {notification && (
          <div className={`no-company-notification no-company-notification-${notification.type}`}>
            <span className="material-symbols-outlined">info</span>
            <span>{notification.message}</span>
            <button
              className="no-company-notification-close"
              onClick={() => setNotification(null)}
              aria-label={t('closeNotification')}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        )}

        <div className="no-company-sections">
          <section className="no-company-section">
            <h2 className="no-company-section-title">{t('companyInvitations')}</h2>
            {companies.length === 0 ? (
              <p className="no-company-empty">{t('noPendingInvitations')}</p>
            ) : (
              <div className="no-company-list">
                {companies.map((company) => (
                  <div key={company.id} className="no-company-card">
                    <div className="no-company-card-content">
                      <h3 className="no-company-card-title">{company.name}</h3>
                      {company.invited_by_name && (
                        <p className="no-company-card-meta">
                          {t('invitedBy', { name: company.invited_by_name })}
                        </p>
                      )}
                      {company.email && (
                        <p className="no-company-card-meta">{company.email}</p>
                      )}
                      {company.address && (
                        <p className="no-company-card-meta">{company.address}</p>
                      )}
                    </div>
                    <button
                      className="sim-button-primary no-company-join-btn"
                      onClick={() => handleJoinCompany(company.id)}
                      disabled={joiningCompanyId === company.id}
                    >
                      {joiningCompanyId === company.id ? t('joining') : t('acceptInvitation')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="no-company-section">
            <h2 className="no-company-section-title">{t('createNewCompany')}</h2>
            <p className="no-company-section-description">{t('createCompanyDescription')}</p>
            <button
              className="sim-button-primary no-company-create-btn"
              onClick={() => setShowCreateModal(true)}
            >
              {t('createCompany')}
            </button>
          </section>
        </div>

        <CreateCompanyModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateCompanySuccess}
        />
      </div>
    </div>
  );
};

export default NoCompany;
